// axiosInstance.js
import axios from 'axios'
import { getToken } from './session'
import { dispatchToast } from '../Components/Toast'

const MAX_RETRIES = 10

// Retry on no-response (network down, timeout, server sleeping) or 5xx server errors.
// Never retry 4xx — those are client/auth errors that won't change on retry.
const shouldRetry = (error) => {
  if (!error.config) return false
  if (!error.response) return true // network error or timeout
  const { status } = error.response
  return status >= 500 || status === 408 || status === 429
}

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
  headers: {},
  timeout: 25000, // 25 s per attempt — keeps stuck requests from hanging forever
})

// Request interceptor — attach the session token (admin password or emp:<id>).
axiosInstance.interceptors.request.use(
  (config) => {
    const token = getToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error),
)

// Response interceptor — automatic retry (immediate, no delay).
// Safe for all methods: GET/PUT/DELETE are idempotent; POST /api/employees uses
// a client-generated employeeId so the server upserts and won't duplicate.
axiosInstance.interceptors.response.use(
  (response) => {
    dispatchToast(response)
    return response
  },
  async (error) => {
    const config = error.config

    if (!shouldRetry(error)) {
      logError(error)
      return Promise.reject(error)
    }

    config._retryCount = (config._retryCount || 0) + 1

    if (config._retryCount > MAX_RETRIES) {
      console.error(`[retry] gave up after ${MAX_RETRIES} attempts: ${config.method?.toUpperCase()} ${config.url}`)
      logError(error)
      return Promise.reject(error)
    }

    console.warn(`[retry ${config._retryCount}/${MAX_RETRIES}] ${config.method?.toUpperCase()} ${config.url}`)
    return axiosInstance(config)
  },
)

function logError(error) {
  if (error.response) {
    console.error('Response Error Status:', error.response.status)
    console.error('Response Data:', error.response.data)
  } else if (error.request) {
    console.error('No Response Received')
  } else {
    console.error('Request Error:', error.message)
  }
}

export default axiosInstance
